<template>
    <CardContentPanel icon="sync-reset" v-bind="{action, index}">
        <template #title>{{ $t('projectclient.projectview.activity.pending.actions.RENEW_PROJECT.title') }}</template>
        <template #body="{freelancerName}">
            {{
                projectInformation.contract.type === 'COST_PLUS' && projectInformation.contract.endDate
                    ? $t('projectclient.projectview.activity.pending.actions.RENEW_PROJECT.description.TIME_AND_MATERIALS', [
                          freelancerName,
                          formatDate(projectInformation.contract.endDate),
                          formatDate(projectInformation.contract.endDate),
                      ])
                    : $t('projectclient.projectview.activity.pending.actions.RENEW_PROJECT.description.FIXED_PRICE', [freelancerName])
            }}
        </template>
        <template #actions>
            <VJoyButton
                :disabled="!isCurrentUserProjectClient && !isPlatformAdmin"
                :loading="dismissingProjectRenewal"
                variant="ghost"
                size="small"
                data-testid="pending-action-card-cta-secondary"
                @click="handleDeclineRenewal"
            >
                {{ $t('projectclient.projectview.activity.pending.actions.RENEW_PROJECT.action.secondary') }}
            </VJoyButton>
            <VJoyButton
                :loading="renewingProject"
                variant="secondary"
                size="small"
                data-testid="pending-action-card-cta-primary"
                @click="handleRenewal"
            >
                {{ $t('projectclient.projectview.activity.pending.actions.RENEW_PROJECT.action.primary') }}
            </VJoyButton>
        </template>
    </CardContentPanel>
    <ClientOpsConfirmationModal
        v-if="isClientOpsDialogVisible"
        v-model="nonParticipantValidationComment"
        header="Please confirm"
        message="You are about to opt out of renewing this project for the client. The action will result in hiding this card for the user permanently."
        @confirm:action="handleOptOut(nonParticipantValidationComment)"
        @close:dialog="handleShowDialog"
    />
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useClientOpsModal} from '@common/composables';
    import {useUserStore} from '@common/stores';
    import {ClientOpsConfirmationModal} from '@common/ui';
    import {storeToRefs} from 'pinia';
    import {useAuth, useFormat} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import CardContentPanel from '../CardContentPanel.vue';
    import {useProjectInformationStore} from '@/common/stores';
    import type {ActivityPendingActionByType} from '@/features/project-view';
    import {useRenewProject} from '@/features/project-view/composables';

    defineProps<{
        action: ActivityPendingActionByType<'RENEW_PROJECT'>;
        index: number;
    }>();

    const authStore = useAuth();
    const userStore = useUserStore();
    const {isPlatformAdmin} = storeToRefs(userStore);

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const isCurrentUserProjectClient = computed(() => authStore.user?.selectedIdentity === projectInformation.value?.client.identityId);

    const {handleRenewal, handleOptOut, renewingProject, dismissingProjectRenewal} = useRenewProject();
    const {formatDate} = useFormat();

    const {nonParticipantValidationComment, isClientOpsDialogVisible, handleShowDialog} = useClientOpsModal();

    function handleDeclineRenewal() {
        if (isCurrentUserProjectClient.value) {
            handleOptOut();
        } else if (isPlatformAdmin.value) {
            handleShowDialog();
        }
    }
</script>
